import React, { ChangeEvent, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Hits, Configure, ClearRefinements, connectRefinementList, ScrollTo } from 'react-instantsearch-dom';

import { GetAlgoliaIndex } from '3-components/Utils/AlgoliaUtils';
import AlgoliaPagination from '3-components/AlgoliaPagination/AlgoliaPagination';
import CustomRefinementListMobile from '4-sections/AlgoliaSearch/widgets/CustomRefinementListMobile';
import { MobileFilterTotalHits } from '4-sections/AlgoliaSearch/widgets/CustomStats';

import ThinHeaderComponent from '4-sections/ThinHeaderBlock/ThinHeaderComponent';
import Button from '3-components/Buttons/Button';
import PropertySaleCard from '3-components/PropertyCard/PropertySaleCard';
import PropertySaleListingMapHits from '3-components/PropertySaleListingMap/PropertySaleListingMapHits';
import Modal from '3-components/Modal/ModalReact';
import Accordion from '3-components/Accordion/AccordionReact';
import Checkbox from '1-core/FormElements/Checkbox';
import { RecentlySoldTimestampLimit } from '3-components/Utils/DateUtils';
import LocationListingFilter from '../LocationListing/LocationListingFilter';
import NoResults from '../LocationListing/widgets/NoResult';
import CustomStats from './widgets/PropertyForSaleCustomStats';

const ALGOLIA_APP_ID = '1K2NLKV3ZJ';
const ALGOLIA_API_ID = '7a32b5b2e4b7dcec55dcb2efc053fedd';

const searchClient = algoliasearch(
    ALGOLIA_APP_ID,
    ALGOLIA_API_ID
);

interface FacetProps {
    attribute: string;
    label: string;
}
interface PropertyForSaleListingProps {
    headerTitle?: string;
    facets: [FacetProps];
    noResultsHeading?: string;
    noResultsSubheading?: string;
    noResultsShowClearButton?: boolean;
    noResultsClearButtonText?: string;
    resultHeadingNote?: string;
}

export type PropertyContextType = {
    bookTourTitle?: string;
    bookTourUrl?: string;
}

export const PropertyStatus = {
    ForSale: 'For sale',
    Sold: 'Sold'
};

const PropertyForSaleListing = (props: PropertyForSaleListingProps) => {
    const {
        headerTitle,
        facets,
        noResultsHeading,
        noResultsSubheading,
        noResultsShowClearButton,
        noResultsClearButtonText,
        resultHeadingNote
    } = props;
    const indexToUse = `${GetAlgoliaIndex('SALE')}`;

    const [searchBarValue, setSearchBarValue] = useState('');

    const searchBarUpdate = (value: string) => {
        setSearchBarValue(value);
        setSearchState({ query: value, page: 1 });
    };

    const [searchState, setSearchState] = useState({ query: '', page: 1, filters: '' });
    const [searchDisplay, setSearchDisplay] = useState('list');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [searchLocation, setSearchLocation] = useState<Record<string, unknown> | undefined | null>(null);

    const closeFilterModal = () => {
        setShowFilterModal(false);
    };
    const openFilterModal = () => {
        setShowFilterModal(true);
    };

    const toggleSearchDisplayMode = (mode: string) => {
        if (mode !== searchDisplay) {
            setSearchDisplay(mode);
        }
    };

    const updateSearchLocation = (point: Record<string, unknown>) => {
        if (point) {
            setSearchLocation({ lat: point.lat, lng: point.lng });
        } else {
            setSearchLocation(null);
        }
    };

    const showFilter = true;
    const displayingMap = searchDisplay === 'map';
    const currentListingHasLocation = true;

    const VirtualRefinementList = connectRefinementList(() => null);
    const [refinementListState, setRefinementListState] = useState({});
    const [suburbs, setSuburbs] = useState([]);
    const [soldToggle, setSoldToggle] = useState(false);

    const clearSearch = () => {
        setSearchBarValue('');
        setSoldToggle(false);
        setSearchState({ query: '', page: 1, filters: `status:"${PropertyStatus.ForSale}"` });
    };

    const GetSuburbData = () => {
        fetch('/assets/data/suburbs.json',
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                }
            })
            .then(response => response.json())
            .then(myJson => {
                if (myJson.data) {
                    const modifiedData = myJson.data.map((loc: Record<string, unknown>) => {
                        return {
                            id: loc.ssc_code,
                            suburb: loc.suburb,
                            postcode: loc.postcode,
                            state: loc.state,
                            state_name: loc.state_name,
                            lat: loc.lat,
                            lng: loc.lng
                        };
                    });
                    setSuburbs(modifiedData);
                }
            });
    };

    useEffect(() => {
        GetSuburbData();
        recentlySoldChange(false);
    }, []);

    let configAdditionalProps: Record<string, unknown> = { hitsPerPage: displayingMap ? 99 : 9 };

    if (!displayingMap && searchLocation && searchLocation.lat && searchLocation.lng) {
        configAdditionalProps = {
            ...configAdditionalProps,
            getRankingInfo: true,
            aroundLatLng: `${searchLocation.lat},${searchLocation.lng}`
        };
    }

    const recentlySoldChange = (checked?: boolean) => {
        const priorTimestamp = RecentlySoldTimestampLimit();

        if (checked) {
            setSearchState({
                ...searchState,
                filters: `status:"${PropertyStatus.Sold}" AND sold_date_timestamp>${priorTimestamp}`
            });
            setSoldToggle(true);
        } else {
            setSearchState({
                ...searchState,
                filters: `status:"${PropertyStatus.ForSale}"`
            });
            setSoldToggle(false);
        }
    };

    const mobileSoldToggelHandle = (e: ChangeEvent) => {
        recentlySoldChange(e.target.checked);
    };

    const clearAutocompleteInputAction = () => {
        const element = document.querySelector('.property-for-sale-listing-results');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <>
            <InstantSearch
                indexName={indexToUse}
                searchClient={searchClient}
                onSearchStateChange={state => {
                    if (showFilterModal && state.refinementList) {
                        setRefinementListState({
                            ...refinementListState,
                            ...state.refinementList
                        });
                    }
                }}
            >
                {displayingMap && (
                    <Configure
                        query={searchState.query}
                        filters={searchState.filters ? searchState.filters : undefined}
                        {...configAdditionalProps}
                    />
                )}
                {!displayingMap && (
                    <Configure
                        query={searchState.query}
                        filters={searchState.filters ? searchState.filters : undefined}
                        {...configAdditionalProps}
                    />
                )}
                {refinementListState && Object.entries(refinementListState).forEach(([key, value]) => {
                    <VirtualRefinementList
                        defaultRefinement={value || []}
                        attribute={key}
                    />;
                })}
                <ThinHeaderComponent
                    title={headerTitle}
                    showSearchbar={true}
                    searchBarValue={searchBarValue}
                    searchBarUpdateHandle={searchBarUpdate}
                    searchBarAutocompleteItems={currentListingHasLocation ? suburbs : undefined}
                    updateSearchLocation={updateSearchLocation}
                    inputAutocompleteActionButtonText="Search"
                    inputAutocompleteActionButtonHandle={clearAutocompleteInputAction}
                />
                <div className="container">
                    <ScrollTo scrollOn="page" searchStatePage={searchState.page}>
                        <div>
                            <div className="result-info">
                                <div className="result-count-info">
                                    <CustomStats isGeoSearchResult={!!searchLocation} resultHeadingNote={resultHeadingNote} />
                                </div>

                                <div className="mode-toggle-container">
                                    {currentListingHasLocation && (
                                        <>
                                            <Button
                                                btnText="List view"
                                                prefixIcon="list"
                                                btnSkin={!displayingMap ? '2' : '3'}
                                                btnSize="sm"
                                                onclick={() => toggleSearchDisplayMode('list')}
                                                btnCustomClass="view-toggle-button"
                                            />
                                            <Button
                                                btnText="Map view"
                                                prefixIcon="map-pin"
                                                btnSkin={displayingMap ? '2' : '3'}
                                                btnSize="sm"
                                                onclick={() => toggleSearchDisplayMode('map')}
                                                btnCustomClass="view-toggle-button"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <LocationListingFilter
                                showFilter={showFilter}
                                facets={facets}
                                openFilterModal={openFilterModal}
                                refinementListState={refinementListState}
                                showRecentlySoldFilter={true}
                                soldToggleValue={soldToggle}
                                soldToggleHandle={recentlySoldChange}
                            />
                        </div>
                        <div className="property-for-sale-listing-results">
                            <NoResults
                                clearSearchAction={clearSearch}
                                noResultsHeading={noResultsHeading}
                                noResultsSubheading={noResultsSubheading}
                                noResultsShowClearButton={noResultsShowClearButton}
                                noResultsClearButtonText={noResultsClearButtonText}
                            />
                            {!displayingMap && (
                                <>
                                    <Hits hitComponent={PropertySaleCard} />
                                    <AlgoliaPagination />
                                </>
                            )}
                            {displayingMap && (
                                <PropertySaleListingMapHits />
                            )}
                        </div>
                    </ScrollTo>
                </div>
                {showFilter && (
                    <Modal
                        isOpen={showFilterModal}
                        onCloseClick={closeFilterModal}
                        ariaLabel=""
                        modalTitle="Filters"
                        overlayCustomClass="mobile-filter-modal-overlay"
                        modalContainerCustomClass="mobile-filter-modal-container"
                    >
                        <hr className="filter-separator" />
                        {facets && facets.length > 0 && (
                            <Accordion>
                                {facets.map(facet => (
                                    <CustomRefinementListMobile
                                        filterAttribute={facet.attribute}
                                        defaultRefinement={refinementListState && refinementListState[facet.attribute]}
                                        label={facet.label}
                                        key={facet.attribute}
                                    />
                                ))}
                                <Checkbox
                                    checkboxId="custom-refinement-checkbox-mobile"
                                    checkboxLabel="Show recently sold"
                                    notRequired={true}
                                    elementCustomClass="mobile-filter-checkbox"
                                    checkboxOnChange={mobileSoldToggelHandle}
                                    checkboxDefaultValue={soldToggle}
                                />
                            </Accordion>
                        )}
                        <hr className="filter-separator black-separator" />
                        <div className="mobile-filter-action">
                            <ClearRefinements translations={{ reset: 'Clear filters' }} />
                            <Button
                                btnText={<MobileFilterTotalHits />}
                                prefixIcon="filter"
                                onclick={closeFilterModal}
                                btnCustomClass="mobile-filter-close-button"
                            />
                        </div>
                    </Modal>
                )}
            </InstantSearch>
        </>
    );
};

const PROPERTY_SALE_LISTING_APP_CLASS = 'property-for-sale-listing-app';
const el = document.getElementById(`${PROPERTY_SALE_LISTING_APP_CLASS}`);
const elProps = el?.dataset.props || '{}';
const propsObj = JSON.parse(elProps);

if (el) {
    ReactDOM.render(
        <PropertyForSaleListing {...propsObj} />,
        el
    );
}
